<template>
  <div class="pointer-events-none relative z-0 mx-2 my-8 block text-center sm:mb-6 sm:mt-12 lg:mx-5 xl:mx-6" @mouseover="hovering = true" @mouseleave="hovering = false">
    <NuxtLink class="pointer-events-auto absolute inset-0 z-[-1] block pt-5 sm:-top-2.5" :to="product.url" :title="product.name">
      <span class="sr-only">{{ product.name }}</span>
    </NuxtLink>

    <!-- note: this may end up conflicting with label pills (discountlabel, giftlabel etc) in the future - if that happens, push it down manually in those cases -->
    <WishlistButton
      :id="product.productId"
      class="btn-medium pointer-events-auto absolute -right-2 -top-2 z-20 block size-9"
      :option-ids="[]"
      :is-added="wishlisted"
      @on-added-to-wishlist="addToWishlist"
      @on-removed-from-wishlist="wishlisted = false"
    />

    <OnImg
      :src="hovering && product.image2 ? product.image2 : product.image"
      :alt="product.name"
      class="aspect-[7/5] h-auto w-full rounded-2xl object-cover"
      :loading="lazyLoaded ? 'lazy' : undefined"
      decoding="async"
      sizes="sm:300px lg:600px"
    />

    <div v-show="isEmployeeMode" class="pointer-events-auto mt-3 cursor-pointer" :class="{ 'text-text-strong': copied }" @click.prevent="copy(product.productId.toString())">
      {{ product.supplierPrefix }}-{{ product.productId }}
    </div>

    <div class="relative -mb-2 mt-1 flex w-full flex-wrap justify-center gap-x-0.5 sm:absolute sm:-top-2.5 sm:left-0 sm:z-10 sm:mt-0">
      <DiscountLabel v-if="product.previousPrice && product.discountPercentage" :discount-percentage="product.discountPercentage" />
      <GiftLabel v-if="product.promotionalLabel" :text="product.promotionalLabel" />
      <Top100Label v-if="product.top100" />
      <NewLabel v-if="product.new" />
      <FastShippingLabel v-if="product.eligibleForFastShipping" />
    </div>

    <Button
      data-miros-entry="custom"
      :data-miros-item="product.productId"
      class="btn-product-search-related btn-rounded btn-condensed pointer-events-auto invisible mt-3 [&.miros-supported]:visible"
      icon-name="miros-search"
      :aria-label="t['w.module.product.use_wordless_search']()"
    >
      <span class="mr-1 text-base">
        <span class="hidden sm:inline">{{ t['w.module.product.use_wordless_search']() }}</span>
        <span class="sm:hidden">{{ t['w.module.product.use_wordless_search.short']() }}</span>
      </span>
    </Button>
    <div class="mx-4 my-3 hyphens-auto text-base">{{ product.name }}</div>

    <div class="flex flex-wrap items-baseline justify-center gap-2">
      <Price v-if="product.previousPrice" class="text-lg font-normal text-text-light line-through" :amount="product.previousPrice" large-fractional exact />
      <Price class="text-lg2 font-medium" :amount="product.price" exact />
    </div>

    <div v-if="product.bestPossiblePrice && getDisplayedPrice(product.bestPossiblePrice) !== getDisplayedPrice(product.price)" class="mt-2 text-sm">
      {{ t['w.module.product.best_price_label']() }} <Price class="text-lg2 font-medium text-text-strong" :amount="product.bestPossiblePrice" exact />
    </div>

    <div v-if="product.afterpaymentPrice" class="text-sm">
      <I18n :message="t['w.module.product.monthly_pay']">
        <template #tax>
          <Price class="text-base font-medium text-text-strong" :amount="product.afterpaymentPrice" large-fractional exact />
        </template>
      </I18n>
    </div>

    <ProductOptions v-if="props.product.options" :product-options="props.product.options" :product-id="product.productId" :lazy-loaded="lazyLoaded" class="mt-4" />
  </div>
</template>

<script setup lang="ts">
import Price from '~/components/price/Price.vue'
import NewLabel from './ProductLabelPillNew.vue'
import GiftLabel from './ProductLabelPillGift.vue'
import Top100Label from './ProductLabelPillTop100.vue'
import FastShippingLabel from './ProductLabelPillShipping.vue'
import DiscountLabel from './ProductPillDiscount.vue'
import ProductOptions from './ProductOptions.vue'
import OnImg from '~/components/on-img/OnImg.vue'
import WishlistButton from '~/components/wishlist-button/WishlistButton.vue'

import type { HallProduct } from './hall-product'
import { useEmployeeMode } from '~/composables/employee-mode'
import { useClipboard } from '@vueuse/core'
import * as t from '~/messages'
import I18n from '~/components/i18n/I18n.vue'
import Button from '~/components/button/Button.vue'
import { useMiros } from '~/composables/miros'

const { isEmployeeMode } = useEmployeeMode()
const { copy, copied } = useClipboard()

const props = defineProps<{
  product: HallProduct
  lazyLoaded: boolean
}>()

useMiros()

const { proxy: gtmProxy } = useGtm()

const wishlisted = ref(props.product.wishlisted)

const hovering = ref<boolean>(false)

const addToWishlist = () => {
  wishlisted.value = true
  sendGtmAddToWishlistEvent()
}

const sendGtmAddToWishlistEvent = () => {
  gtmProxy.dataLayer.push({
    event: 'addToWishlist',
    ecommerce: {
      currencyCode: useCurrencyCode(),
      add: {
        products: [
          {
            name: props.product.name,
            id: props.product.productId,
            price: props.product.price,
          },
        ],
      },
    },
  })
}

const getDisplayedPrice = (price: number | null) => {
  if (!price) {
    return null
  }

  return (price / 100000).toFixed(2)
}
</script>
